import dayjs, { Dayjs, extend } from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/de';
dayjs.extend(LocalizedFormat)
extend(utc);
extend(timezone);
dayjs.locale('de');
dayjs.tz.setDefault('UTC');


export async function getSignedImageUrl(key: string): Promise<string | null> {
    try {
        const res = await fetch(`/api/admin/images/sign?key=${encodeURIComponent(key)}`);
        if (!res.ok) return null;
        const data = await res.json();
        return data.url ?? null;
    } catch (error) {
        console.error('Error getting signed image URL:', error);
        return null;
    }
}

export function unixToGerman(time: number, format = "DD.MM.YYYY HH:mm"): string {
    return dayjs.unix(time).tz('Europe/Berlin').format(format);
}


export function germanDayjs(): Dayjs {
    return dayjs.utc().tz("Europe/Berlin");
}

export function isFutureDate(date: Dayjs | string): boolean {
    return dayjs.utc(date).tz('Europe/Berlin').isAfter(dayjs.utc().tz('Europe/Berlin'));
}

export function toRelativeDate(date: Dayjs | string, type?: 'full', difference?: number): string {
    let targetTime = dayjs.utc(date).tz('Europe/Berlin').startOf('day');
    let now = dayjs.utc().tz('Europe/Berlin').startOf('day');
    let diffDays = targetTime.diff(now, 'days');
    let formattedTime = dayjs.utc(date).tz('Europe/Berlin').format('HH:mm') + " Uhr";
    let formattedDate = dayjs.utc(date).tz('Europe/Berlin').format('dddd, DD.MMMM');

    //display just the date if the difference is greater than 7 days or less than -7 days
    let normalDateFormat;
    if (difference && (diffDays > difference || diffDays < -difference)) {
        normalDateFormat = formattedDate + `${type === "full" ? " " + formattedTime : ""}`;
        return normalDateFormat;
    }

    let relativeDateFormat
    if (diffDays === 2) {
        relativeDateFormat = `übermorgen ${type === "full" ? " " + formattedTime : ""}`;
    } else if (diffDays === 1) {
        relativeDateFormat = `morgen ${type === "full" ? " " + formattedTime : ""}`;
    } else if (diffDays === 0) {
        relativeDateFormat = `heute ${type === "full" ? " " + formattedTime : ""}`;
    } else if (diffDays === -1) {
        relativeDateFormat = `gestern ${type === "full" ? " " + formattedTime : ""}`;
    }
    else if (diffDays > 0 && diffDays <= 7) {
        relativeDateFormat = `in ${diffDays} Tagen ${type === "full" ? " " + formattedTime : ""}`;
    } else if (diffDays > 7) {
        relativeDateFormat = formattedDate + `${type === "full" ? " " + formattedTime : ""}`;
    } else if (diffDays < -1 && diffDays >= -7) {
        relativeDateFormat = `vor ${Math.abs(diffDays)} Tagen ${type === "full" ? " " + formattedTime : ""}`;
    } else if (diffDays < -7) {
        relativeDateFormat = formattedDate + `${type === "full" ? " " + formattedTime : ""}`;
    } else {
        relativeDateFormat = formattedDate + `${type === "full" ? " " + formattedTime : ""}`;
    }
    return relativeDateFormat;
}



export const formatCurrency = (amount: number): string => {
    return Number(amount / 100).toLocaleString("de-DE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

export const priceList = () => {
    return {
        "pkwday": 1000,
        "four": 500,
        "womo": 2000,
        "caravan": 3500,
        "pkwnight": 1500,
        "kurtaxe": 300,
        "hund": 800,
        "surferbowl": 800,
        "lachs": 850,
        "halbebroetchen": 750,
        "card": 200,
        "paypal": 200,
        "paypalpremium": 200,
        "giropay": 200,
        "ideal": 200,
        "klarna": 200,
        "apple_pay": 200,
        "google_pay": 200,
    }
}

export const itemDescriptions = (): { [key: string]: string } => {
    return {
        pkwday: "Tagesticket",
        four: "4 Stunden Ticket",
        womo: "Übernachtung (WOMO/PKW)",
        caravan: "PKW + Caravan",
        pkwnight: "PKW Abstellen",
        kurtaxe: "Kurtaxe",
        hund: "Hund",
        surferbowl: "Surfer Bowl",
        lachs: "Lachs",
        halbebroetchen: "Halbes Brötchen",
        google_pay: "Google Pay",
        giropay: "Giropay",
        klarna: "Klarna",
        apple_pay: "Apple Pay",
        sepa_debit: "SEPA Debit",
        ideal: "Ideal",
        card: "Card",
        paypal: "Paypal",
    }
}

// Tickets for an actual overnight guest stay. These are the only ones subject to
// Kurtaxe and AVS Meldeschein registration. Plain parking (pkwnight) is NOT a
// guest stay and is therefore exempt.
export const GUEST_STAY_TICKETS = ['womo', 'caravan'] as const;

// A booking requires guest registration (traveler names, Kurtaxe and AVS
// Meldeschein) only when it is a guest stay AND it is not a Nachzahlung. A
// Nachzahlung settles a previous night that was already registered and taxed, so
// it must not request travelers or Kurtaxe again.
export function requiresGuestRegistration(ticket: string, recursive: boolean): boolean {
    return (GUEST_STAY_TICKETS as readonly string[]).includes(ticket) && !recursive;
}

// Preserve international names and punctuation, but reject email addresses
// accidentally entered in the traveler-name field.
export function hasValidTravelerName(name: string): boolean {
    const stripped = name.trim();
    if (stripped.length === 0 || stripped.includes("@")) return false;
    return /\p{Letter}/u.test(stripped);
}

// Kurtaxe is 3,00 EUR per person (age 15+) per night.
export function calculateKurtaxe(numberOfTravelers: number, numberOfNights: number): number {
    const price = priceList()["kurtaxe"];
    return numberOfTravelers * numberOfNights * price;
}

export function calculateTotal(items: { id: string; quantity: number }[]) {
    const prices = priceList();
    const total = items.reduce((acc: number, item: { id: string; quantity: number }) => {
        const price = prices[item.id as keyof typeof prices];
        return acc + item.quantity * (price || 0);
    }, 0);

    return total;
}

export function isHourInRange(hour: number, startHour: number, endHour: number): boolean {
    if (startHour === endHour) {
        return true;
    }

    if (startHour < endHour) {
        return hour >= startHour && hour <= endHour;
    }

    return hour >= startHour || hour <= endHour;
}
